import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Badge,
  TextField,
  IconButton,
  Toolbar,
  Stack,
  Divider,
  Menu,
  MenuItem
} from '@mui/material';

import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  ShoppingCart,
  Inventory,
  AssignmentTurnedIn,
  LocalShipping,
  CheckCircle
} from '@mui/icons-material';

import { motion } from 'framer-motion';
import Navbar from '../navbar/navbar';
import FooterBar from '../footer/footerBar';

const mockData = {
  novos: [{ codigo: '001', cliente: 'João Silva', data: '17/07/2025', valor: '49,90', telefone: '81999999999', endereco: 'Rua A, 123', formaDePagamento: 'Cartão' }],
  separacao: [
    { codigo: '311', cliente: 'Luciano', data: '17/07/2025', valor: '27,90', telefone: '81996417868' },
    { codigo: '312', cliente: 'Luciano', data: '17/07/2025', valor: '27,90', telefone: '81996417868' }
  ],
  prontos: [{ codigo: '501', cliente: 'Maria Lima', data: '17/07/2025', valor: '32,00', observacao: 'Sem cebola' }],
  transito: [{ codigo: '600', cliente: 'Carlos Sousa', motoqueiro: 'Pedro', status: 'Saindo para entrega' }],
  entregue: [{ codigo: '204', cliente: 'Damião', data: '17/07/2025', valor: '23,50', telefone: '81986666666' }]
};

const statusLabels = [
  { key: 'novos', label: 'Novos', icon: <ShoppingCart /> },
  { key: 'separacao', label: 'Separação', icon: <Inventory /> },
  { key: 'prontos', label: 'Prontos', icon: <AssignmentTurnedIn /> },
  { key: 'transito', label: 'Deslocamento', icon: <LocalShipping /> },
  { key: 'entregue', label: 'Entregue', icon: <CheckCircle /> }
];

function PedidosDelivery() {
  const [statusAtual, setStatusAtual] = useState('novos');
  const [pesquisa, setPesquisa] = useState('');

  // Dropdown state
  const [anchorEl, setAnchorEl] = useState(null);
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);

  const handleTabChange = (_, newValue) => {
    setStatusAtual(newValue);
    setPesquisa('');
  };

  const handleMenuOpen = (event, pedido) => {
    setAnchorEl(event.currentTarget);
    setPedidoSelecionado(pedido);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setPedidoSelecionado(null);
  };

  const pedidos = mockData[statusAtual].filter((pedido) =>
    pedido.cliente?.toLowerCase().includes(pesquisa.toLowerCase())
  );

  return (
    <>
      <Navbar />
      <Toolbar />

      <Box sx={{ px: 4, py: 3, maxWidth: '100%' }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Pedidos do Delivery
        </Typography>

        {/* Ações do topo */}
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" mb={3} gap={2}>
          <Box display="flex" gap={2}>
            <IconButton color="primary"><RefreshIcon /></IconButton>
            <IconButton color="primary"><AddIcon /></IconButton>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <SearchIcon />
            <TextField
              size="medium"
              placeholder="Pesquisar cliente..."
              value={pesquisa}
              onChange={(e) => setPesquisa(e.target.value)}
              variant="outlined"
              sx={{ minWidth: '250px' }}
            />
          </Box>
        </Box>

        {/* Abas de status */}
        <Tabs
          value={statusAtual}
          onChange={handleTabChange}
          variant="fullWidth"
          scrollButtons
          allowScrollButtonsMobile
          sx={{ mb: 3 }}
        >
          {statusLabels.map(({ key, label, icon }) => (
            <Tab
              key={key}
              value={key}
              label={
                <Badge badgeContent={mockData[key].length} color="secondary">
                  <Box display="flex" alignItems="center" gap={1}>
                    {icon} <strong>{label}</strong>
                  </Box>
                </Badge>
              }
              sx={{ fontSize: '1rem', py: 2 }}
            />
          ))}
        </Tabs>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="h6" gutterBottom>
          {statusLabels.find((s) => s.key === statusAtual)?.label}
        </Typography>

        {/* Lista de pedidos em linhas */}
        <Stack spacing={0}>
          {pedidos.length === 0 ? (
            <Typography variant="body1" color="text.secondary">
              Nenhum pedido encontrado.
            </Typography>
          ) : (
            pedidos.map((pedido, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  borderBottom="1px solid #ccc"
                  py={2}
                  px={1}
                  sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">{pedido.cliente}</Typography>
                    <Typography variant="body1">Valor: R$ {pedido.valor}</Typography>
                    {pedido.data && <Typography variant="body1">Data: {pedido.data}</Typography>}
                    {pedido.endereco && <Typography variant="body1">Endereço: {pedido.endereco}</Typography>}
                    {pedido.telefone && <Typography variant="body1">Telefone: {pedido.telefone}</Typography>}
                    {pedido.observacao && <Typography variant="body1">Obs: {pedido.observacao}</Typography>}
                    {pedido.formaDePagamento && <Typography variant="body1">Pagamento: {pedido.formaDePagamento}</Typography>}
                    {pedido.motoqueiro && <Typography variant="body1">Motoqueiro: {pedido.motoqueiro}</Typography>}
                    {pedido.status && <Typography variant="body1">Status: {pedido.status}</Typography>}
                  </Box>

                  {/* Botão de menu apenas se não for entregue */}
                  {statusAtual !== 'entregue' && (
                    <>
                      <IconButton onClick={(e) => handleMenuOpen(e, pedido)}>
                        <MoreVertIcon />
                      </IconButton>
                    </>
                  )}
                </Box>
              </motion.div>
            ))
          )}
        </Stack>

        {/* Menu de ações */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => { handleMenuClose(); console.log('Abrir modal para ação 1'); }}>
            Ação 1
          </MenuItem>
          <MenuItem onClick={() => { handleMenuClose(); console.log('Abrir modal para ação 2'); }}>
            Ação 2
          </MenuItem>
        </Menu>
      </Box>

      <FooterBar />
    </>
  );
}

export default PedidosDelivery;
